import * as React from 'react';
import { useEffect } from 'react';
import { QuickTagCreationWizardPageProps } from '../QuickTagCreationWizard';
import Select from 'react-select';
import { uniq } from 'lodash';
import { QuickTagFileColumnDescriptor } from '@nexxt/common/types';

/**
 * QuickTagCreationWizard Page for Dataset Configuration.
 */
function QuickTagConfig(props: QuickTagCreationWizardPageProps) {
    const sheetNames = uniq(
        props.datasetFileDescriptors
            .map((descriptor) => descriptor.sheetName)
            .filter((sheetName) => sheetName?.length)
    );
    const sheetNameSelectObjs = sheetNames.map((sheetName) => ({
        value: sheetName,
        label: sheetName
    }));

    /** <Select> objs for all descriptors that contain supported langId values. */
    const langDescriptorSelectObjs = props.datasetFileDescriptors
        .filter((d) => d.isLangId)
        .filter(
            (d) =>
                !d.sheetName?.length || props.selectedSheetName == d.sheetName
        )
        .map((d) => {
            return { value: d.header, label: d.header };
        });
    /** <Select> objs for all descriptors except for language id. */
    const descriptorSelectObjs = props.datasetFileDescriptors
        .filter(
            (d) => !langDescriptorSelectObjs.some((h) => h.value == d.header)
        )
        .filter(
            (d) =>
                !d.sheetName?.length || props.selectedSheetName == d.sheetName
        )
        .map((d) => {
            return { value: d.header, label: d.header };
        });

    /** Returns true if @selectObj matches @d descriptor. */
    const doesSelectObjMatchDescriptor = (
        selectObj: { value: string; label: string },
        d: QuickTagFileColumnDescriptor
    ): boolean => {
        return (
            d?.header == selectObj.value &&
            (!props.selectedSheetName ||
                props.selectedSheetName == d?.sheetName)
        );
    };

    /** Reset descriptors when sheet name is changed. */
    useEffect(() => {
        props.setSelectedVerbatimDescriptors([]);
        props.setSelectedLangDescriptor(undefined);
        props.setSelectedIdDescriptor(undefined);
    }, [props.selectedSheetName]);

    return (
        <>
            {Boolean(props.selectedSheetName.length) && (
                <>
                    <label className="text-sm mb-1">Sheet Name</label>
                    <Select
                        className="mb-2"
                        size="small"
                        value={sheetNameSelectObjs.find(
                            (obj) => obj.label == props.selectedSheetName
                        )}
                        options={sheetNameSelectObjs}
                        onChange={(selectedOption) =>
                            props.setSelectedSheetName(selectedOption.label)
                        }
                    />
                </>
            )}
            <label className="text-sm mb-1">Verbatim texts</label>
            <Select
                className="mb-2"
                size="small"
                options={descriptorSelectObjs}
                isMulti
                value={descriptorSelectObjs.filter((obj) =>
                    props.selectedVerbatimDescriptors.some((d) =>
                        doesSelectObjMatchDescriptor(obj, d)
                    )
                )}
                onChange={(selectedObjs) =>
                    props.setSelectedVerbatimDescriptors(
                        props.datasetFileDescriptors.filter((d) =>
                            selectedObjs.some((obj) =>
                                doesSelectObjMatchDescriptor(obj, d)
                            )
                        )
                    )
                }
            />
            {props.selectedVerbatimDescriptors.length > 1 && (
                <p>
                    Note: each verbatim variable selected will create its own
                    QuickTag instance.
                </p>
            )}
            <label className="text-sm mb-1">
                Verbatim unique IDs (optional)
            </label>
            <Select
                className="mb-2"
                size="small"
                options={descriptorSelectObjs}
                noOptionsMessage={() => 'No unique IDs found.'}
                value={
                    descriptorSelectObjs.find(
                        (obj) => obj.label == props.selectedIdDescriptor?.header
                    ) || null
                }
                onChange={(selectedObj) =>
                    props.setSelectedIdDescriptor(
                        props.datasetFileDescriptors.find((d) =>
                            doesSelectObjMatchDescriptor(selectedObj, d)
                        )
                    )
                }
            />
            <label className="text-sm mb-1">Language IDs (optional)</label>
            <Select
                className="mb-2"
                size="small"
                options={langDescriptorSelectObjs}
                noOptionsMessage={() => 'No language IDs found.'}
                value={
                    langDescriptorSelectObjs.find((obj) =>
                        doesSelectObjMatchDescriptor(
                            obj,
                            props.selectedLangDescriptor
                        )
                    ) || null
                }
                onChange={(selectedObj) =>
                    props.setSelectedLangDescriptor(
                        props.datasetFileDescriptors.find((d) =>
                            doesSelectObjMatchDescriptor(selectedObj, d)
                        )
                    )
                }
            />
        </>
    );
}

export default QuickTagConfig;
